/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import * as coreHttp from "@azure/core-http";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { SearchServiceClient } from "../searchServiceClient";
import {
  SearchIndexerDataSource,
  DataSourcesCreateOrUpdateOptionalParams,
  DataSourcesCreateOrUpdateResponse,
  DataSourcesDeleteOptionalParams,
  DataSourcesGetOptionalParams,
  DataSourcesGetResponse,
  DataSourcesListOptionalParams,
  DataSourcesListResponse,
  DataSourcesCreateOptionalParams,
  DataSourcesCreateResponse
} from "../models";

/** Class representing a DataSources. */
export class DataSources {
  private readonly client: SearchServiceClient;

  /**
   * Initialize a new instance of the class DataSources class.
   * @param client Reference to the service client
   */
  constructor(client: SearchServiceClient) {
    this.client = client;
  }

  /**
   * Creates a new datasource or updates a datasource if it already exists.
   * @param dataSourceName The name of the datasource to create or update.
   * @param dataSource The definition of the datasource to create or update.
   * @param options The options parameters.
   */
  createOrUpdate(
    dataSourceName: string,
    dataSource: SearchIndexerDataSource,
    options?: DataSourcesCreateOrUpdateOptionalParams
  ): Promise<DataSourcesCreateOrUpdateResponse> {
    const operationArguments: coreHttp.OperationArguments = {
      dataSourceName,
      dataSource,
      options: coreHttp.operationOptionsToRequestOptionsBase(options || {})
    };
    return this.client.sendOperationRequest(
      operationArguments,
      createOrUpdateOperationSpec
    ) as Promise<DataSourcesCreateOrUpdateResponse>;
  }

  /**
   * Deletes a datasource.
   * @param dataSourceName The name of the datasource to delete.
   * @param options The options parameters.
   */
  delete(
    dataSourceName: string,
    options?: DataSourcesDeleteOptionalParams
  ): Promise<coreHttp.RestResponse> {
    const operationArguments: coreHttp.OperationArguments = {
      dataSourceName,
      options: coreHttp.operationOptionsToRequestOptionsBase(options || {})
    };
    return this.client.sendOperationRequest(
      operationArguments,
      deleteOperationSpec
    ) as Promise<coreHttp.RestResponse>;
  }

  /**
   * Retrieves a datasource definition.
   * @param dataSourceName The name of the datasource to retrieve.
   * @param options The options parameters.
   */
  get(
    dataSourceName: string,
    options?: DataSourcesGetOptionalParams
  ): Promise<DataSourcesGetResponse> {
    const operationArguments: coreHttp.OperationArguments = {
      dataSourceName,
      options: coreHttp.operationOptionsToRequestOptionsBase(options || {})
    };
    return this.client.sendOperationRequest(
      operationArguments,
      getOperationSpec
    ) as Promise<DataSourcesGetResponse>;
  }

  /**
   * Lists all datasources available for a search service.
   * @param options The options parameters.
   */
  list(
    options?: DataSourcesListOptionalParams
  ): Promise<DataSourcesListResponse> {
    const operationArguments: coreHttp.OperationArguments = {
      options: coreHttp.operationOptionsToRequestOptionsBase(options || {})
    };
    return this.client.sendOperationRequest(
      operationArguments,
      listOperationSpec
    ) as Promise<DataSourcesListResponse>;
  }

  /**
   * Creates a new datasource.
   * @param dataSource The definition of the datasource to create.
   * @param options The options parameters.
   */
  create(
    dataSource: SearchIndexerDataSource,
    options?: DataSourcesCreateOptionalParams
  ): Promise<DataSourcesCreateResponse> {
    const operationArguments: coreHttp.OperationArguments = {
      dataSource,
      options: coreHttp.operationOptionsToRequestOptionsBase(options || {})
    };
    return this.client.sendOperationRequest(
      operationArguments,
      createOperationSpec
    ) as Promise<DataSourcesCreateResponse>;
  }
}
// Operation Specifications
const serializer = new coreHttp.Serializer(Mappers, /* isXml */ false);

const createOrUpdateOperationSpec: coreHttp.OperationSpec = {
  path: "/datasources('{dataSourceName}')",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.SearchIndexerDataSource
    },
    201: {
      bodyMapper: Mappers.SearchIndexerDataSource
    },
    default: {
      bodyMapper: Mappers.SearchError
    }
  },
  requestBody: Parameters.dataSource,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.dataSourceName],
  headerParameters: [
    Parameters.contentType,
    Parameters.accept,
    Parameters.xMsClientRequestId,
    Parameters.ifMatch,
    Parameters.ifNoneMatch,
    Parameters.prefer
  ],
  mediaType: "json",
  serializer
};
const deleteOperationSpec: coreHttp.OperationSpec = {
  path: "/datasources('{dataSourceName}')",
  httpMethod: "DELETE",
  responses: {
    204: {},
    404: {},
    default: {
      bodyMapper: Mappers.SearchError
    }
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.dataSourceName],
  headerParameters: [
    Parameters.accept,
    Parameters.xMsClientRequestId,
    Parameters.ifMatch,
    Parameters.ifNoneMatch
  ],
  serializer
};
const getOperationSpec: coreHttp.OperationSpec = {
  path: "/datasources('{dataSourceName}')",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.SearchIndexerDataSource
    },
    default: {
      bodyMapper: Mappers.SearchError
    }
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.dataSourceName],
  headerParameters: [Parameters.accept, Parameters.xMsClientRequestId],
  serializer
};
const listOperationSpec: coreHttp.OperationSpec = {
  path: "/datasources",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ListDataSourcesResult
    },
    default: {
      bodyMapper: Mappers.SearchError
    }
  },
  queryParameters: [Parameters.apiVersion, Parameters.select],
  urlParameters: [Parameters.endpoint],
  headerParameters: [Parameters.accept, Parameters.xMsClientRequestId],
  serializer
};
const createOperationSpec: coreHttp.OperationSpec = {
  path: "/datasources",
  httpMethod: "POST",
  responses: {
    201: {
      bodyMapper: Mappers.SearchIndexerDataSource
    },
    default: {
      bodyMapper: Mappers.SearchError
    }
  },
  requestBody: Parameters.dataSource,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint],
  headerParameters: [
    Parameters.contentType,
    Parameters.accept,
    Parameters.xMsClientRequestId
  ],
  mediaType: "json",
  serializer
};
